import React, { useState } from 'react'
import { Modal, Button, Typography, Space, Steps } from 'antd'
import {
  ClockCircleOutlined,
  ApartmentOutlined,
  SlidersOutlined,
  SafetyCertificateOutlined,
  LeftOutlined,
  RightOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

const { Title, Paragraph, Text } = Typography

// 兩個庫存類型卡片，中間箭頭 + 觸發條件 chip；右下角接「自動建立庫存轉換單」說明
function Ch2Diagram({ t, prefix }) {
  const cardBase = {
    flex: 1,
    minWidth: 0,
    borderRadius: 10,
    padding: '14px 16px',
    textAlign: 'center',
  }
  const sourceCard = {
    ...cardBase,
    background: '#fff',
    border: '1px solid #d9d9d9',
    color: '#333',
  }
  const targetCard = {
    ...cardBase,
    background: '#fff7e6',
    border: '1px solid #ffd591',
    color: '#d46b08',
  }
  const cardLabel = { fontSize: 12, color: '#999', marginBottom: 6 }
  const cardValue = { fontSize: 15, fontWeight: 600 }
  const cardExample = { fontSize: 12, color: '#999', marginTop: 4 }

  return (
    <div
      style={{
        background: '#f6f8fa',
        border: '1px solid #e8e8e8',
        borderRadius: 8,
        padding: '20px 24px',
        margin: '16px 0',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={sourceCard}>
          <div style={cardLabel}>{t(`${prefix}.ch2_diagram_source_label`)}</div>
          <div style={cardValue}>📦 {t(`${prefix}.ch2_diagram_source_example`)}</div>
        </div>

        <ArrowRightOutlined style={{ fontSize: 20, color: '#bfbfbf', flexShrink: 0 }} />

        <div style={targetCard}>
          <div style={cardLabel}>{t(`${prefix}.ch2_diagram_target_label`)}</div>
          <div style={cardValue}>📦 {t(`${prefix}.ch2_diagram_target_example`)}</div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 6,
          marginTop: 14,
          fontSize: 12,
          color: '#666',
        }}
      >
        <div>
          <ClockCircleOutlined style={{ color: '#1890ff', marginRight: 6 }} />
          {t(`${prefix}.ch2_diagram_check_when`)}
        </div>
        <div>
          <span style={{ color: '#52c41a', marginRight: 6 }}>✓</span>
          {t(`${prefix}.ch2_diagram_check_what`)}
        </div>
        <div style={cardExample}>↳ {t(`${prefix}.ch2_diagram_outcome`)}</div>
      </div>
    </div>
  )
}

export default function InventoryTypeReclassifyRuleConceptModal({ visible, onClose }) {
  const { t } = useTranslation()
  const [currentStep, setCurrentStep] = useState(0)

  const prefix = 'concepts.inventory_type_reclassify_rules'

  const chapters = [
    {
      icon: <ClockCircleOutlined style={{ fontSize: 28, color: '#fa8c16' }} />,
      titleKey: `${prefix}.ch1_title`,
      paragraphs: [
        `${prefix}.ch1_p1`,
        `${prefix}.ch1_p2`,
        `${prefix}.ch1_p3`,
      ],
    },
    {
      icon: <ApartmentOutlined style={{ fontSize: 28, color: '#1890ff' }} />,
      titleKey: `${prefix}.ch2_title`,
      paragraphs: [
        `${prefix}.ch2_p1`,
      ],
      bullets: [
        `${prefix}.ch2_b1`,
        `${prefix}.ch2_b2`,
        `${prefix}.ch2_b3`,
      ],
      customDiagram: <Ch2Diagram t={t} prefix={prefix} />,
      paragraphsAfter: [
        `${prefix}.ch2_p2`,
      ],
    },
    {
      icon: <SlidersOutlined style={{ fontSize: 28, color: '#722ed1' }} />,
      titleKey: `${prefix}.ch3_title`,
      paragraphs: [
        `${prefix}.ch3_p1`,
        `${prefix}.ch3_p2`,
      ],
      bullets: [
        `${prefix}.ch3_b1`,
        `${prefix}.ch3_b2`,
      ],
      diagram: `${prefix}.ch3_diagram`,
      paragraphsAfter: [
        `${prefix}.ch3_p3`,
      ],
    },
    {
      icon: <SafetyCertificateOutlined style={{ fontSize: 28, color: '#52c41a' }} />,
      titleKey: `${prefix}.ch4_title`,
      paragraphs: [
        `${prefix}.ch4_p1`,
      ],
      bullets: [
        `${prefix}.ch4_b1`,
        `${prefix}.ch4_b2`,
        `${prefix}.ch4_b3`,
        `${prefix}.ch4_b4`,
      ],
      paragraphsAfter: [
        `${prefix}.ch4_p2`,
      ],
    },
  ]

  const chapter = chapters[currentStep]
  const isFirst = currentStep === 0
  const isLast = currentStep === chapters.length - 1

  const handleClose = () => {
    setCurrentStep(0)
    onClose()
  }

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      closable={false}
      width={720}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {currentStep + 1} / {chapters.length}
          </Text>
          <Space>
            {!isFirst && (
              <Button icon={<LeftOutlined />} onClick={() => setCurrentStep(s => s - 1)}>
                {t(`${prefix}.prev`)}
              </Button>
            )}
            {isLast ? (
              <Button type="primary" onClick={handleClose}>
                {t(`${prefix}.got_it`)}
              </Button>
            ) : (
              <Button type="primary" onClick={() => setCurrentStep(s => s + 1)}>
                {t(`${prefix}.next`)} <RightOutlined />
              </Button>
            )}
          </Space>
        </div>
      }
    >
      <Steps
        current={currentStep}
        size="small"
        onChange={(step) => setCurrentStep(step)}
        style={{ marginBottom: 32 }}
        items={chapters.map((ch, i) => ({
          title: i === currentStep ? t(ch.titleKey) : '',
        }))}
      />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 24 }}>
        {chapter.icon}
        <Title level={3} style={{ margin: 0 }}>
          {t(chapter.titleKey)}
        </Title>
      </div>

      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        {chapter.paragraphs?.map((key, i) => (
          <Paragraph key={i} style={{ fontSize: 15, lineHeight: 1.8, color: '#333' }}>
            {t(key)}
          </Paragraph>
        ))}

        {chapter.bullets && (
          <ul style={{ paddingLeft: 20, marginBottom: 16 }}>
            {chapter.bullets.map((key, i) => (
              <li key={i} style={{ fontSize: 15, lineHeight: 2, color: '#333' }}>
                {t(key)}
              </li>
            ))}
          </ul>
        )}

        {chapter.diagram && (
          <div style={{
            background: '#f6f8fa',
            border: '1px solid #e8e8e8',
            borderRadius: 8,
            padding: '20px 24px',
            margin: '16px 0',
            fontFamily: 'monospace',
            fontSize: 14,
            lineHeight: 1.8,
            whiteSpace: 'pre-line',
          }}>
            {t(chapter.diagram)}
          </div>
        )}

        {chapter.customDiagram}

        {chapter.paragraphsAfter?.map((key, i) => (
          <Paragraph key={`after-${i}`} style={{ fontSize: 15, lineHeight: 1.8, color: '#333' }}>
            {t(key)}
          </Paragraph>
        ))}
      </div>
    </Modal>
  )
}
